import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { KeyRound, User, ArrowRight } from "lucide-react";
import { loginSocio } from "@/data/socioAuthStore";
import { PUBLIC } from "@/constants/testIds";

/**
 * Login del socio contra backend real (`/api/socios/auth/login`).
 * Al éxito la cookie HttpOnly de sesión la emite el backend y en
 * `ct_current_socio_v1`.
 */
export default function Login() {
  const navigate = useNavigate();
  const [numeroSocio, setNumeroSocio] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!numeroSocio.trim() || !password) {
      setError("Ingresa tu número de socio y contraseña.");
      return;
    }
    setLoading(true);
    const session = await loginSocio(numeroSocio, password);
    setLoading(false);
    if (!session) {
      setError("Número de socio o contraseña incorrectos.");
      return;
    }
    if (session.error === "network") {
      setError("No se pudo conectar con el servidor. Intenta más tarde.");
      return;
    }
    navigate("/app");
  };

  return (
    <div
      data-testid={PUBLIC.loginScreen}
      className="min-h-[70vh] flex items-center"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 w-full">
        {/* Copy lateral */}
        <div className="hidden lg:flex flex-col gap-6 animate-fade-up">
          <span className="overline text-accent">Acceso de socios</span>
          <h1 className="font-heading text-4xl xl:text-5xl leading-[1.05] tracking-tight">
            Bienvenido de vuelta al club.
          </h1>
          <p className="text-muted-foreground leading-relaxed max-w-md">
            Inicia sesión con tu número de socio para acceder a tus eventos,
            boletos y beneficios exclusivos del Casino Tampiqueño.
          </p>
          <div className="divider-gold max-w-32" />
        </div>

        {/* Formulario */}
        <div className="animate-fade-up delay-100">
          <div className="rounded-2xl border hairline bg-card p-8 md:p-10 shadow-soft">
            <div className="lg:hidden mb-6">
              <span className="overline text-accent">Acceso de socios</span>
              <h1 className="mt-2 font-heading text-3xl leading-tight">
                Iniciar sesión
              </h1>
            </div>
            <h2 className="hidden lg:block font-heading text-2xl mb-6">
              Iniciar sesión
            </h2>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="numero-socio"
                  className="text-sm font-medium text-foreground"
                >
                  Número de socio
                </label>
                <div className="relative">
                  <User
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                    strokeWidth={1.6}
                  />
                  <input
                    id="numero-socio"
                    type="text"
                    autoComplete="username"
                    data-testid={PUBLIC.inputSocio}
                    value={numeroSocio}
                    onChange={(e) => setNumeroSocio(e.target.value)}
                    placeholder="Ej. 1024"
                    className="w-full pl-11 pr-4 py-3 rounded-xl bg-background border hairline text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="password"
                  className="text-sm font-medium text-foreground"
                >
                  Contraseña
                </label>
                <div className="relative">
                  <KeyRound
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                    strokeWidth={1.6}
                  />
                  <input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    data-testid={PUBLIC.inputPassword}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-11 pr-4 py-3 rounded-xl bg-background border hairline text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all"
                  />
                </div>
              </div>

              {error && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}

              <button
                type="submit"
                data-testid={PUBLIC.btnEntrar}
                disabled={loading}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft transition-all duration-200 active:scale-[0.98] hover:opacity-90 disabled:opacity-70"
              >
                {loading ? "Verificando…" : "Entrar"}
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            </form>

            <div className="mt-6 flex items-center justify-start text-sm">
              <button
                type="button"
                data-testid={PUBLIC.linkOlvidePassword}
                onClick={() => navigate("/activar")}
                className="text-primary dark:text-accent hover:underline underline-offset-4"
              >
                Activar cuenta / Olvidé mi contraseña
              </button>
            </div>
          </div>

          <p className="mt-6 text-xs text-muted-foreground text-center lg:text-left">
            ¿Aún no eres socio?{" "}
            <Link
              to="/"
              className="text-primary dark:text-accent hover:underline underline-offset-4"
            >
              Volver al inicio
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
